// ============================================
// MissionControl/MissionControl.utils.js
// ============================================

import { DEPLOYMENT_STATUS, MISSION_MODES, MISSION_TIERS } from './MissionControl.constants';

// ============================================
// Duration & Time Utilities
// ============================================

export const convertDurationToDays = (duration) => {
  if (!duration) return 1;
  
  // Handle if duration is already a number
  if (typeof duration === 'number') return duration;
  
  // Handle non-string types
  if (typeof duration !== 'string') return 1;
  
  const match = duration.match(/(\d+)\s*(day|days|hour|hours)/i);
  if (!match) return 1;
  
  const value = parseInt(match[1]);
  const unit = match[2].toLowerCase();
  
  if (unit.includes('hour')) {
    return Math.ceil(value / 24);
  }
  
  return value;
};

// ============================================
// Squad & Soldier Utilities
// ============================================

export const getReadySquadMembers = (squad) => {
  if (!Array.isArray(squad)) return [];
  
  return squad.filter(soldier => 
    soldier.status !== DEPLOYMENT_STATUS.ON_MISSION && 
    soldier.health > 0
  );
};

export const canDeployMission = (mission, selectedSquad) => {
  if (!mission || !Array.isArray(selectedSquad)) return false;
  
  const squadSize = selectedSquad.length;
  const { minSquad, maxSquad } = mission.requirements || {};
  
  return squadSize >= (minSquad || 1) && squadSize <= (maxSquad || 999);
};

export const validateDeployment = (mission, squad) => {
  return canDeployMission(mission, squad);
};

// ============================================
// Mission Availability
// ============================================

export const checkMissionAvailability = (mission, squad, currentDay) => {
  const isUnlocked = currentDay >= (mission.unlockDay || 1);
  const availableSquad = getReadySquadMembers(squad);
  const hasEnoughSquad = availableSquad.length >= (mission.requirements?.minSquad || 1);
  
  let reason = '';
  if (!hasEnoughSquad) {
    reason = `Need at least ${mission.requirements?.minSquad || 1} available soldiers`;
  }
  
  return {
    isUnlocked,
    isAvailable: hasEnoughSquad,
    canSelect: isUnlocked && hasEnoughSquad,
    reason
  };
};

export const getMissionAvailability = (mission, squad, currentDay) => {
  return checkMissionAvailability(mission, squad, currentDay);
};

// ============================================
// Visual/Display Utilities
// ============================================

export const getDifficultyColor = (tier, color) => {
  const colorMap = {
    [MISSION_TIERS.SIMPLE]: 'text-green-400 border-green-500',
    [MISSION_TIERS.COMPLEX]: 'text-yellow-400 border-yellow-500',
    [MISSION_TIERS.CAMPAIGN]: 'text-red-400 border-red-500',
  };
  
  return colorMap[tier] || `text-${color}-400 border-${color}-500`;
};

export const getRiskColor = (risk) => {
  if (typeof risk === 'string') {
    const lowerRisk = risk.toLowerCase();
    if (lowerRisk === 'low') return 'text-green-400';
    if (lowerRisk === 'medium' || lowerRisk === 'moderate') return 'text-yellow-400';
    if (lowerRisk === 'high') return 'text-red-400';
  }
  
  return 'text-gray-400';
};

// ============================================
// Serialization Utilities
// ============================================

export const createSerializableMission = (mission) => {
  if (!mission) return null;
  
  return {
    id: mission.id,
    name: mission.name,
    description: mission.description,
    location: mission.location,
    duration: mission.duration,
    tier: mission.tier,
    color: mission.color,
    requirements: mission.requirements,
    rewards: mission.rewards,
    risks: mission.risks,
    storyContext: mission.storyContext,
    unlockDay: mission.unlockDay,
  };
};

export const createSerializableSquad = (squad) => {
  if (!Array.isArray(squad)) return [];
  
  return squad.map(soldier => ({
    id: soldier.id,
    name: soldier.name,
    rank: soldier.rank,
    health: soldier.health,
    status: soldier.status,
    xp: soldier.xp,
    level: soldier.level,
  }));
};

// ============================================
// Deployment Utilities
// ============================================

export const createDeploymentPayload = (mission, squad, currentCampaign, mode) => {
  return {
    mission: {
      ...mission,
      squad,
      days_remaining: convertDurationToDays(mission.duration),
      status: DEPLOYMENT_STATUS.ACTIVE,
      deployed_on_day: currentCampaign?.day || 1,
      id: `mission-${Date.now()}`,
    },
    squad: squad.map(member => ({
      ...member,
      status: DEPLOYMENT_STATUS.ON_MISSION,
    })),
    mode,
  };
};

export const getMissionDaysRequired = (mission) => {
  return convertDurationToDays(mission.duration);
};

// ============================================
// Mission Handlers
// ============================================

export const prepareManualDeployment = (
  selectedMission,
  selectedSquadMembers,
  currentCampaign,
  theme
) => {
  const serializableMission = createSerializableMission(selectedMission);
  const serializableSquad = createSerializableSquad(selectedSquadMembers);

  console.log('🚀 Preparing manual deployment:', {
    campaign: currentCampaign,
    theme: theme,
    mission: serializableMission.name,
    squadSize: serializableSquad.length
  });

  return {
    state: {
      campaign: currentCampaign, // FIXED: Pass the full campaign object
      mission: serializableMission,
      squad: serializableSquad,
      campaignId: currentCampaign?.id,
      campaignName: currentCampaign?.name,
      day: currentCampaign?.day,
      mode: MISSION_MODES.MANUAL,
      theme: theme,
    },
    path: `/${theme}/mission`,
  };
};